// Module-level access-token provider for the datasets-api and pv-api clients,
// registered by each app via setApiClientsTokenProvider(). Both APIs now require
// a Kinde access token (audience `inrange-api` - the same one BMA validates), so
// these clients need one too.
//
// Registered rather than threaded through every hook signature, mirroring
// setTokenRefresher / setTokenValidator / setOnUnauthorized in
// building-manager-api-client: it keeps this package Kinde-agnostic (it has no
// @kinde-oss dependency and sits below the component layer) and avoids changing
// the signature of every hook and every one of their call sites across both apps.
//
// The apps register the same getAuthTokenBlocking-backed closure they already use
// for BMA, so token refresh, validity checking and the relogin gate are inherited
// rather than reimplemented here.

let tokenProvider: (() => Promise<string>) | null = null;

export const setApiClientsTokenProvider = (
  provider: (() => Promise<string>) | null
): void => {
  tokenProvider = provider;
};

/**
 * Thrown when a request is made before an app has registered a provider.
 *
 * React Query's retry (3 attempts, see queryOptions) covers the narrow window
 * between first render and the registering effect, so a genuine race self-heals
 * rather than surfacing to the user.
 */
export class NoTokenProviderError extends Error {
  constructor() {
    super(
      "No api-clients token provider registered. The app must call " +
        "setApiClientsTokenProvider() before calling datasets-api or pv-api."
    );
    this.name = "NoTokenProviderError";
  }
}

export const getApiClientsToken = async (): Promise<string> => {
  if (!tokenProvider) throw new NoTokenProviderError();
  return await tokenProvider();
};

/**
 * Authorization header for a datasets-api / pv-api request.
 *
 * Deliberately per-request rather than set on axios.defaults: presigned S3 URLs
 * are fetched with the same axios instance, and S3 rejects a request that carries
 * both a presigned query signature and an Authorization header ("Only one auth
 * mechanism allowed"). See usePresignedUrlData, which must stay unauthenticated.
 */
export const apiClientsAuthHeaders = async (): Promise<{
  Authorization: string;
}> => ({
  Authorization: `Bearer ${await getApiClientsToken()}`,
});
